/**
 * Google Preferred Sources — https://developers.google.com/search/docs/appearance/preferred-sources
 *
 * We deliberately skip Google's auto-rendered widget (`<div google-add-preferred-source-btn>`)
 * and drive the flow from our own button, so the CTA can live inside the site's `.btn` system.
 * The library is injected in manual mode, on demand, and every failure path falls back to the
 * plain deeplink — the button works with the script blocked, timed out, or absent entirely.
 */

import { SITE_DOMAIN } from '../constants';

/** No-JS / blocked-script fallback. Only domain- and subdomain-level sites are eligible. */
export const PREFERRED_SOURCE_URL = `https://www.google.com/preferences/source?q=${SITE_DOMAIN}`;

const LIBRARY_URL = 'https://news.google.com/swg/js/v1/publisher.js';
const LOAD_TIMEOUT_MS = 4000;

export interface PreferredSourceApi {
  init: (options: { theme?: 'light' | 'dark'; lang?: string }) => void;
  addPreferredSource: () => void;
}

declare global {
  interface Window {
    PREFERRED_SOURCE?: ((api: PreferredSourceApi) => void)[];
  }
}

let pending: Promise<PreferredSourceApi | null> | null = null;

/**
 * Injects the library once and resolves with the API, or `null` if it never arrives.
 * Memoized: repeated calls share the single injection.
 */
export function loadPreferredSource(): Promise<PreferredSourceApi | null> {
  if (pending) return pending;

  pending = new Promise<PreferredSourceApi | null>((resolve) => {
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        resolve(null);
        return;
      }

      let settled = false;
      const settle = (api: PreferredSourceApi | null) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(api);
      };

      const timer = window.setTimeout(() => settle(null), LOAD_TIMEOUT_MS);

      // The callback queue must exist before the script runs; the library drains it on boot.
      (window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || []).push((api) => {
        try {
          api.init({ theme: 'dark', lang: 'en' });
          settle(api);
        } catch {
          settle(null);
        }
      });

      const script = document.createElement('script');
      script.src = LIBRARY_URL;
      script.async = true;
      // Suppresses Google's own button; we render the CTA ourselves.
      script.setAttribute('preferred-sources-control', 'manual');
      script.onerror = () => settle(null);
      document.head.appendChild(script);
    } catch {
      resolve(null);
    }
  });

  return pending;
}
